import React, { useState } from 'react';
import moment from 'moment';
import { Popover } from '@mui/material';
import {
    evalModeEnums,
    firstTrialResponseEnums,
    goalDataTypeEnums,
    Images,
    isMaintenanceCompleted,
    isMaintenanceRunning,
    masteryDirectionEnums,
    masteryEvalTypeEnums,
    targetStatusEnums,
} from 'utils';

const DATE_TIME = 'MMM D, YYYY · h:mm A';
const DATE = 'MMM D, YYYY';
const directionLabels = {
    [masteryDirectionEnums.ABOVE]: 'at or above',
    [masteryDirectionEnums.BELOW]: 'at or below',
    [masteryDirectionEnums.EQUAL]: 'exactly',
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** When the target last became MASTERED, from its status history. */
const masteredAt = (target) =>
    [...(target?.statusHistory || [])].reverse().find((h) => h?.newStatus === targetStatusEnums.MASTERED)?.createdAt;

/** One step rule in words: "First trial correct · 1 session", "≥ 90% average · 3 sessions". */
const ruleText = ({ config, goal, direction, value, across, firstTrialCount, forward }) => {
    const unit = config?.evalMode === evalModeEnums.BY_DAY ? 'day' : 'session';
    const total = config?.evalType === masteryEvalTypeEnums.TOTAL;
    if (config?.firstTrialOnly) {
        const wanted = config?.firstTrialResponse === firstTrialResponseEnums.INCORRECT ? 'incorrect' : 'correct';
        const response = forward ? wanted : `not ${wanted}`;
        const window = total ? `${firstTrialCount ?? across} of ${plural(across, unit)}` : `${plural(across, unit)} in a row`;
        return `First trial ${response} · ${window}`;
    }
    const percent = goal?.dataType === goalDataTypeEnums.PERCENT ? '%' : '';
    const window = total ? `average over ${plural(across, unit)}` : `${plural(across, unit)} in a row`;
    return `${directionLabels[direction] || ''} ${value}${percent} · ${window}`;
};

/**
 * Info button next to a mastered target's maintenance step: when maintenance and the current step
 * started, the check schedule and when the next check is due, how many checks the step has had,
 * and the rules that move it. `config` is the maintenance config the target follows (its own or the goal's).
 */
export const MaintenanceInfo = ({ target, goal, config }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const running = isMaintenanceRunning(target);
    if (!running && !isMaintenanceCompleted(target)) return null;

    const state = target.maintenanceState;
    const steps = config?.steps || [];
    // The API sends the step 1-based.
    const stepNumber = state?.maintenanceStep || 1;
    const step = steps[stepNumber - 1];
    const byDay = config?.evalMode === evalModeEnums.BY_DAY;
    const every = step?.showTargetEvery || 0;
    const since = state?.sessionsSinceCheck || 0;
    const progress = Math.round(state?.stepProgress || 0);
    const mastered = masteredAt(target);

    let nextCheck = '—';
    let due = false;
    if (byDay && state?.showDate) {
        due = !moment(state.showDate).isAfter(moment(), 'day');
        nextCheck = due ? 'Due today' : moment(state.showDate).format(DATE);
    } else if (!byDay && every) {
        const left = every - since;
        due = left <= 1;
        nextCheck = due ? 'Due next session' : `in ${plural(left, 'session')}`;
    }

    const close = (e) => {
        e?.stopPropagation();
        setAnchorEl(null);
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                className="maintenance-info-btn"
                aria-label="Maintenance details"
                onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                }}
            >
                <img src={Images.toolInfo} alt="" />
            </button>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div className="maintenance-info-card">
                    {running ? (
                        <>
                            <div className="maintenance-info-header">
                                <p className="maintenance-info-title">
                                    Maintenance · Step {stepNumber} of {steps.length}
                                </p>
                                <div className="maintenance-info-progress">
                                    <div
                                        className={progress < 0 ? 'back' : 'next'}
                                        style={{ width: `${Math.min(Math.abs(progress), 100)}%` }}
                                    />
                                </div>
                                <p className="maintenance-info-muted">
                                    {progress < 0 ? `${Math.abs(progress)}% toward stepping back` : `${progress}% toward the next step`}
                                </p>
                            </div>

                            <div className="maintenance-info-section">
                                {mastered && <InfoRow label="Mastered" value={moment(mastered).format(DATE_TIME)} />}
                                {state?.dayOfTheMove && (
                                    <InfoRow label="This step since" value={moment(state.dayOfTheMove).format(DATE_TIME)} />
                                )}
                            </div>

                            <div className="maintenance-info-section">
                                <InfoRow label="Schedule" value={every ? `every ${plural(every, byDay ? 'day' : 'session')}` : '—'} />
                                {!byDay && !!every && <InfoRow label="Since last check" value={`${since} of ${plural(every, 'session')}`} />}
                                <InfoRow label="Next check" value={nextCheck} highlight={due} />
                                <InfoRow label="Checks in this step" value={state?.checksInStep || 0} />
                            </div>

                            {step && (
                                <div className="maintenance-info-section">
                                    <InfoRow
                                        label={stepNumber === steps.length ? '↑ Complete' : '↑ Next step'}
                                        value={ruleText({
                                            config,
                                            goal,
                                            direction: step.moveNextIfDirection,
                                            value: step.moveNextIfValue,
                                            across: step.moveNextAcross,
                                            firstTrialCount: step.firstTrialNextCount,
                                            forward: true,
                                        })}
                                    />
                                    <InfoRow
                                        label={stepNumber === 1 ? '↓ Back to Teaching' : '↓ Step back'}
                                        value={ruleText({
                                            config,
                                            goal,
                                            direction: step.moveBackIfDirection,
                                            value: step.moveBackIfValue,
                                            across: step.moveBackAcross,
                                            firstTrialCount: step.firstTrialBackCount,
                                            forward: false,
                                        })}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="maintenance-info-section">
                            <p className="maintenance-info-title">✓ Maintenance completed</p>
                            <InfoRow label="Completed" value={moment(state.completedAt).format(DATE_TIME)} />
                            {mastered && <InfoRow label="Mastered" value={moment(mastered).format(DATE_TIME)} />}
                        </div>
                    )}
                </div>
            </Popover>
        </div>
    );
};

const InfoRow = ({ label, value, highlight }) => (
    <div className="maintenance-info-row">
        <span className="maintenance-info-label">{label}</span>
        <span className={`maintenance-info-value ${highlight ? 'due' : ''}`}>
            {highlight && <span className="maintenance-info-dot" />}
            {value}
        </span>
    </div>
);
